import react from 'react';
import { LineChart } from '@components/chart/lineChart';
import * as am4charts from '@amcharts/amcharts4/charts';
import moment from 'moment-timezone';
import { drawXAxisLabels, drawYAxisLabelsLineChart, getData, getTooltipAdapter } from './lib';
import { useTranslation } from '@lib/useTypedTranslation';
import { ITzSmartBatteryLevel } from '../../../services/shared/battery/batteryCentricService';
import './styles.css';

interface ICommonBatteryLevelChartProps {
  date: number,
  isLoading?: boolean
}

// `displayTimeZone` selects the data shape at the type boundary: zoned callers must supply records
// carrying a real `utc` instant (and `date` is a real UTC instant within the selected local day),
// ordinary callers keep the device-local `local` shape.
export type IBatteryLevelChart = ICommonBatteryLevelChartProps & (
  { displayTimeZone?: undefined, data: IBatteryLevelData[] } |
  { displayTimeZone: string, data: ITzSmartBatteryLevel[] }
);

export interface IBatteryLevelData {
  local: string,
  level: number,
  smartBattery?: {
    serialNumber: string
  },
  device: {
    serialNumber: string
  }
}

export const BatteryLevelChart = (props: IBatteryLevelChart) => {
  const { t } = useTranslation(['translation', 'performance']);
  const title = t(`BATTERY_CHARGE_LEVEL_FOR`, { date: moment.utc(props.date).tz(props.displayTimeZone || 'UTC').format('Do MMMM'), ns: 'translation' });
  const lineAndToolTipColour = '#5ECAEA';
  const chartData = props.displayTimeZone ? getData(props.data as ITzSmartBatteryLevel[], props.displayTimeZone) : getData(props.data as IBatteryLevelData[]);

  return (
    <div className="battery-charge-level-chart-container">
      <LineChart<any>
        cypressId="batteryChargeLevelChart"
        data={chartData}
        series={[
          { visible: true,
            dataKey: 'level',
            description: t('CHARGE_LEVEL'),
            colour: lineAndToolTipColour
          }
        ]}
        title={title}
        className = "battery-charge-level"
        hasLegend={true}
        useAxisTooltip={true}
        drawXAxis={(_, xAxis: am4charts.DateAxis) => drawXAxisLabels(xAxis, props.date, props.displayTimeZone)}
        drawYAxis={(_, yAxis: am4charts.ValueAxis) => drawYAxisLabelsLineChart(yAxis)}
        adjustChartDimensions={{ length: 97.5, height: 240 }}
        hasNoSelectionOverlay={!props.isLoading}
        getSeriesToolTipAdapter={() => getTooltipAdapter(t, props.displayTimeZone)}
      />
    </div>
  );
};
